import React, { useState } from "react";
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, FormHelperText, Link, MenuItem, Typography } from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  Receipt as GstIcon,
  CreditCard as PanIcon,
  Category as TypeIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  LocationCity as CityIcon,
  Map as StateIcon,
  PinDrop as PinIcon,
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import { postData } from "../../Webservice/Webservice";
import { goldButtonSx, fadeUp, slideIn } from "./signinStyles";
import { Field, PasswordField, OtpBox, StepDots, rules, BUSINESS_TYPES, INDIAN_STATES } from "./SignupParts";

const iconSx = { color: "#C8A054", fontSize: 20 };
const twoCols = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 1.5 };

// Business sign up in three short steps so each one fits the screen without scrolling:
// 1 account owner, 2 business details, 3 registered address and terms. Then the email is confirmed with a code.
const STEPS = ["Owner", "Business", "Address"];
const STEP_FIELDS = [
  ["name", "email", "password", "confirmPassword"],
  ["businessName", "businessType", "businessRegistrationNumber", "gstNumber", "panNumber"],
  ["addressLine1", "city", "state", "pincode", "terms"],
];

function BusinessSignupForm({ onDone, onError }) {
  const [stage, setStage] = useState("details"); // "details" | "verify" | "done"
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: "", email: "", password: "", confirmPassword: "",
    businessName: "", businessType: "", businessRegistrationNumber: "", gstNumber: "", panNumber: "",
    addressLine1: "", addressLine2: "", city: "", state: "", pincode: "",
    terms: false,
  });
  const [errors, setErrors] = useState({});
  const [show, setShow] = useState({ password: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [emailOk, setEmailOk] = useState(false);

  const set = (name, transform) => (e) => {
    let value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    if (transform) value = transform(value);
    setForm((f) => ({ ...f, [name]: value }));
    onError("");
    if (errors[name]) setErrors((er) => { const n = { ...er }; delete n[name]; return n; });
  };
  const upper = (v) => v.toUpperCase().replace(/\s/g, "");

  // The business email and a password are the only required fields. Everything else is optional,
  // but a value that is filled in must still look right (email, GSTIN, PAN, pincode, matching passwords).
  const validateStep = (index) => {
    const e = {};
    if (!rules.email.test(form.email.trim())) e.email = "Enter a valid business email";
    if (form.password.length < 8) e.password = "At least 8 characters";
    if (form.confirmPassword && form.confirmPassword !== form.password) e.confirmPassword = "Passwords do not match";
    if (form.gstNumber && !rules.gstin.test(form.gstNumber)) e.gstNumber = "Enter a valid 15-character GSTIN";
    if (form.panNumber && !rules.pan.test(form.panNumber)) e.panNumber = "Enter a valid PAN (like ABCDE1234F)";
    if (form.pincode && !rules.pincode.test(form.pincode)) e.pincode = "Enter a valid 6-digit pincode";
    const here = {};
    STEP_FIELDS[index].forEach((k) => { if (e[k]) here[k] = e[k]; });
    setErrors(here);
    return Object.keys(here).length === 0;
  };

  const payload = () => {
    const address = {
      ...(form.addressLine1.trim() && { addressLine1: form.addressLine1.trim() }),
      ...(form.addressLine2.trim() && { addressLine2: form.addressLine2.trim() }),
      ...(form.city.trim() && { city: form.city.trim() }),
      ...(form.state && { state: form.state }),
      ...(form.pincode && { pincode: form.pincode }),
    };
    return {
      type: "email",
      customerType: "business",
      name: form.name.trim() || form.email.trim().split("@")[0],
      email: form.email.trim(),
      password: form.password,
      ...(form.businessName.trim() && { businessName: form.businessName.trim() }),
      ...(form.businessType && { businessType: form.businessType }),
      ...(form.businessRegistrationNumber.trim() && { businessRegistrationNumber: form.businessRegistrationNumber.trim() }),
      ...(form.gstNumber && { gstNumber: form.gstNumber }),
      ...(form.panNumber && { panNumber: form.panNumber }),
      ...(Object.keys(address).length > 0 && { businessAddress: { ...address, country: "India" } }),
    };
  };

  const next = (ev) => {
    ev.preventDefault();
    onError("");
    if (!validateStep(step)) return;
    if (step < STEPS.length - 1) setStep(step + 1);
    else submit();
  };

  const submit = async () => {
    setLoading(true);
    try {
      const response = await postData("customer/register", payload(), "", false, false);
      if (response && !response.error) {
        setEmailOk(false);
        setStage("verify");
      } else {
        onError(response?.message || "Registration failed");
      }
    } catch (err) {
      onError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail = async (code) => {
    const response = await postData("customer/verify-otp", { email: form.email.trim(), code, customerType: "business" }, "", false, false);
    if (response && !response.error) {
      setEmailOk(true);
      setStage("done");
      setTimeout(onDone, 1800);
    } else {
      throw new Error(response?.message || "Invalid OTP");
    }
  };

  // asking again for an unverified business account sends a fresh code
  const resend = async () => {
    const response = await postData("customer/register", payload(), "", false, false);
    if (response?.error) throw new Error(response.message || "Could not resend the code");
  };

  if (stage === "done") {
    return (
      <Box sx={{ textAlign: "center", py: 3, animation: `${fadeUp} 0.5s both` }}>
        <CheckIcon sx={{ fontSize: 64, color: "#2e7d32" }} />
        <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>Email verified</Typography>
        <Typography variant="body2" color="text.secondary">Your business account is created. Taking you to sign in…</Typography>
      </Box>
    );
  }

  if (stage === "verify") {
    return (
      <Box sx={{ animation: `${slideIn} 0.5s both` }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, textAlign: "center" }}>
          Confirm your business email to finish. The code is valid for 5 minutes.
        </Typography>
        <OtpBox icon={<EmailIcon sx={{ fontSize: 20 }} />} title="Business email" target={form.email.trim()} verified={emailOk} onVerify={verifyEmail} onResend={resend} />
        <Typography variant="body2" align="center" sx={{ mt: 1 }}>
          <Link component="button" type="button" underline="hover" onClick={() => { onError(""); setStage("details"); setStep(2); }} sx={{ color: "#8a6a24", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <ArrowBackIcon sx={{ fontSize: 17 }} />
            Edit my details
          </Link>
        </Typography>
      </Box>
    );
  }

  const last = step === STEPS.length - 1;

  return (
    <Box component="form" noValidate onSubmit={next}>
      <StepDots steps={STEPS} current={step} />
      <Typography variant="body2" align="center" sx={{ mb: 0.5, fontSize: "0.78rem", color: "text.secondary" }}>
        {step === 0 ? "Email and password are all we need. The rest is optional." : "Optional: add it now or later from your profile."}
      </Typography>

      {/* each step re-appears softly */}
      <Box key={step} sx={{ animation: `${fadeUp} 0.45s both` }}>
        {step === 0 && (
          <>
            <Field name="name" label="Owner / contact name" value={form.name} onChange={set("name")} error={errors.name} icon={<PersonIcon sx={iconSx} />} />
            <Field name="email" label="Business email" type="email" value={form.email} onChange={set("email")} error={errors.email} icon={<EmailIcon sx={iconSx} />} />
            <Box sx={twoCols}>
              <PasswordField name="password" label="Password" value={form.password} onChange={set("password")} error={errors.password} icon={<LockIcon sx={iconSx} />} show={show.password} onToggle={() => setShow((s) => ({ ...s, password: !s.password }))} />
              <PasswordField name="confirmPassword" label="Confirm password" value={form.confirmPassword} onChange={set("confirmPassword")} error={errors.confirmPassword} icon={<LockIcon sx={iconSx} />} show={show.confirm} onToggle={() => setShow((s) => ({ ...s, confirm: !s.confirm }))} />
            </Box>
          </>
        )}

        {step === 1 && (
          <>
            <Field name="businessName" label="Business / company name" value={form.businessName} onChange={set("businessName")} error={errors.businessName} icon={<BusinessIcon sx={iconSx} />} />
            <Box sx={twoCols}>
              <Field name="businessType" label="Business type" select value={form.businessType} onChange={set("businessType")} error={errors.businessType} icon={<TypeIcon sx={iconSx} />}>
                {BUSINESS_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Field>
              <Field name="businessRegistrationNumber" label="Registration number" value={form.businessRegistrationNumber} onChange={set("businessRegistrationNumber")} error={errors.businessRegistrationNumber} icon={<BadgeIcon sx={iconSx} />} />
              <Field name="gstNumber" label="GSTIN" required={false} value={form.gstNumber} onChange={set("gstNumber", upper)} error={errors.gstNumber} icon={<GstIcon sx={iconSx} />} inputProps={{ maxLength: 15 }} />
              <Field name="panNumber" label="PAN number" value={form.panNumber} onChange={set("panNumber", upper)} error={errors.panNumber} icon={<PanIcon sx={iconSx} />} inputProps={{ maxLength: 10 }} />
            </Box>
          </>
        )}

        {step === 2 && (
          <>
            <Field name="addressLine1" label="Address line 1" value={form.addressLine1} onChange={set("addressLine1")} error={errors.addressLine1} icon={<HomeIcon sx={iconSx} />} />
            <Field name="addressLine2" label="Address Line 2" required={false} value={form.addressLine2} onChange={set("addressLine2")} error={errors.addressLine2} icon={<LocationIcon sx={iconSx} />} />
            <Box sx={twoCols}>
              <Field name="city" label="City" value={form.city} onChange={set("city")} error={errors.city} icon={<CityIcon sx={iconSx} />} />
              <Field name="state" label="State" select value={form.state} onChange={set("state")} error={errors.state} icon={<StateIcon sx={iconSx} />}>
                {INDIAN_STATES.map((st) => <MenuItem key={st} value={st}>{st}</MenuItem>)}
              </Field>
            </Box>
            <Field name="pincode" label="Pincode" value={form.pincode} onChange={set("pincode", (v) => v.replace(/\D/g, "").slice(0, 6))} error={errors.pincode} icon={<PinIcon sx={iconSx} />} inputProps={{ inputMode: "numeric", maxLength: 6 }} />

            <FormControlLabel
              sx={{ mt: -0.5, mr: 0 }}
              control={<Checkbox size="small" checked={form.terms} onChange={set("terms")} sx={{ color: "#C8A054", "&.Mui-checked": { color: "#C8A054" } }} />}
              label={
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                  I agree to the{" "}
                  <Link href="/terms-and-conditions" target="_blank" underline="hover" sx={{ color: "#8a6a24", fontWeight: 600 }}>Terms &amp; Conditions</Link>{" "}and{" "}
                  <Link href="/privacy-policy" target="_blank" underline="hover" sx={{ color: "#8a6a24", fontWeight: 600 }}>Privacy Policy</Link>
                </Typography>
              }
            />
            {errors.terms && <FormHelperText error sx={{ ml: 1.75, mt: -0.5 }}>{errors.terms}</FormHelperText>}
          </>
        )}
      </Box>

      {step === 0 && (
        <Typography variant="body2" align="center" sx={{ mt: 0.5 }}>
          <Link component="button" type="button" underline="hover" onClick={() => { onError(""); if (validateStep(0)) submit(); }} sx={{ color: "#8a6a24", fontWeight: 600, fontSize: "0.82rem" }}>
            Skip the rest &amp; create my account
          </Link>
        </Typography>
      )}

      <Box sx={{ display: "flex", gap: 1, mt: 1.25 }}>
        {step > 0 && (
          <Button type="button" variant="outlined" onClick={() => { onError(""); setErrors({}); setStep(step - 1); }} startIcon={<ArrowBackIcon />}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, px: 2, color: "#8a6a24", borderColor: "rgba(200,160,84,0.5)", "&:hover": { borderColor: "#C8A054", bgcolor: "#fbf3df" } }}>
            Back
          </Button>
        )}
        <Button type="submit" fullWidth variant="contained" disabled={loading}
          startIcon={loading || !last ? null : <PersonAddIcon />} endIcon={loading || last ? null : <ArrowForwardIcon />} sx={goldButtonSx}>
          {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : last ? "Create business account" : "Next"}
        </Button>
      </Box>
    </Box>
  );
}

export default BusinessSignupForm;
